import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { DEFAULT_MESSAGE_PAGE_SIZE } from '../constants';
import { fetchChatMessagesGap } from '../utils/fetchChatMessagesGap';
import { gapFillChatOnReconnect } from '../utils/gapFillChatOnReconnect';
import { handleChatSocketCloseEvent } from '../utils/handleChatSocketCloseEvent';
import { handleChatSocketMessageEvent } from '../utils/handleChatSocketMessageEvent';
import { mergeGapMessagesIntoInfiniteCache } from '../utils/mergeGapMessagesIntoInfiniteCache';
import { deriveWsUrl } from '../utils/wsUrl';
import { APICommunicator } from '~/api/APICommunicator';
import { queryKeys } from '~/utils/queryKeys';

const RECONNECT_DELAY_MS = 3000;

type UseChatSocketOptions = {
    lastSequenceRef?: RefObject<number | null>;
    onForbidden?: () => void;
};

// Per-dialog WebSocket — gap-fills via `getMessages({ after: lastSequence })` on reconnect.
export function useChatSocket(chatId: string | undefined, options?: UseChatSocketOptions): void {
    const queryClient = useQueryClient();

    const stoppedRef = useRef(false);
    const socketRef = useRef<WebSocket | null>(null);
    const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const optionsRef = useRef(options);
    optionsRef.current = options;

    useEffect(() => {
        if (!chatId) return undefined;
        stoppedRef.current = false;

        const invalidateChat = () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.getChatMessages(chatId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.getChats() });
        };

        const gapFillOnReconnect = async () => {
            await gapFillChatOnReconnect(chatId, optionsRef.current?.lastSequenceRef?.current, {
                fetchGap: fetchChatMessagesGap,
                invalidateChat,
                limit: DEFAULT_MESSAGE_PAGE_SIZE,
                mergeGap: (gapMessages) =>
                    mergeGapMessagesIntoInfiniteCache(
                        queryClient,
                        chatId,
                        gapMessages as Awaited<ReturnType<typeof fetchChatMessagesGap>>,
                        DEFAULT_MESSAGE_PAGE_SIZE,
                    ),
                queryClient,
            });
        };

        const scheduleReconnect = (connect: () => void) => {
            if (stoppedRef.current) return;
            reconnectTimeoutRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
        };

        const connect = () => {
            if (stoppedRef.current) return;

            const ws = new WebSocket(deriveWsUrl(`/ws/chat/${chatId}`));
            socketRef.current = ws;

            ws.onopen = () => {
                gapFillOnReconnect().catch(() => undefined);
            };

            ws.onmessage = (event) => {
                let message: { type?: string } | undefined;
                try {
                    message = JSON.parse(event.data as string);
                } catch {
                    return;
                }
                handleChatSocketMessageEvent(message?.type, invalidateChat);
            };

            ws.onclose = (event) => {
                if (stoppedRef.current) return;

                handleChatSocketCloseEvent(event.code, {
                    onForbidden: () => optionsRef.current?.onForbidden?.(),
                    renew: async () => (await APICommunicator.renew()) === true,
                    scheduleReconnect: () => scheduleReconnect(connect),
                });
            };
        };

        connect();

        return () => {
            stoppedRef.current = true;
            clearTimeout(reconnectTimeoutRef.current);
            socketRef.current?.close();
        };
    }, [chatId, queryClient]);
}
